<template>
  <div class="q-search"
       :class="[
         `q-search--${size}`,
         `q-search--${variant}`,
         {
           'q-search--active': isActive,
           'q-search--has-value': hasValue,
           'q-search--disabled': disabled,
           'q-search--full-width': fullWidth,
         },
       ]"
  >
    <div class="q-search__content">
      <div class="q-search__input-wrapper">
        <MagnifyingGlassIcon class="q-search__icon q-search__icon--search"
                             :class="iconColorClass"
        />
        <input ref="inputRef"
               v-model="inputValue"
               :placeholder="placeholder"
               :disabled="disabled"
               type="text"
               class="q-search__input"
               v-bind="$attrs"
               @focus="handleFocus"
               @blur="handleBlur"
               @input="handleInput"
               @keydown.enter="handleEnter"
        />
      </div>
      <button v-if="hasValue && !disabled"
              type="button"
              class="q-search__clear-btn"
              @click="clearInput"
              @mousedown.prevent
      >
        <XIcon class="q-search__icon q-search__icon--clear"
               :class="iconColorClass"
        />
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, nextTick } from 'vue';
import MagnifyingGlassIcon from '../../../icons/MagnifyingGlass.vue';
import XIcon from '../../../icons/X.vue';

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: '',
  },
  placeholder: {
    type: String,
    default: 'Поиск...',
  },
  size: {
    type: String,
    default: 'm',
    validator: (value) => ['s', 'm', 'l'].includes(value),
  },
  variant: {
    type: String,
    default: 'primary',
    validator: (value) => ['primary', 'secondary', 'tertiary'].includes(value),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'search', 'clear', 'focus', 'blur']);

const inputRef = ref(null);
const isActive = ref(false);

const inputValue = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
  },
});

const hasValue = computed(() => {
  return inputValue.value && inputValue.value.toString().length > 0;
});

const iconColorClass = computed(() => {
  if (props.disabled) return 'q-search__icon--disabled';
  if (isActive.value) return 'q-search__icon--active';
  if (hasValue.value) return 'q-search__icon--has-value';
  return 'q-search__icon--default';
});

function handleFocus(event) {
  if (props.disabled) return;
  isActive.value = true;
  emit('focus', event);
}

function handleBlur(event) {
  if (props.disabled) return;
  isActive.value = false;
  emit('blur', event);
}

function handleInput(event) {
  emit('update:modelValue', event.target.value);
}

function handleEnter(event) {
  emit('search', inputValue.value);
}

function clearInput() {
  inputValue.value = '';
  emit('clear');
  nextTick(() => {
    inputRef.value?.focus();
  });
}

// Expose methods for external use
defineExpose({
  focus: () => inputRef.value?.focus(),
  blur: () => inputRef.value?.blur(),
  select: () => inputRef.value?.select(),
  clear: clearInput,
});
</script>

<style lang="scss" scoped>
.q-search {
  position: relative;
  width: 100%;
  transition: all 0.2s ease;

  &__content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    border-radius: var(--border-radius);
    background: var(--background-color);
    height: var(--height-search);
    border: 1px solid var(--border-color);
    padding: var(--content-padding);
    transition: all 0.2s ease;
  }

  &__input-wrapper {
    display: flex;
    align-items: center;
    gap: var(--icon-gap);
    flex: 1;
    padding: 0;
  }

  &__input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    color: var(--text-color);
    font-family: Inter, sans-serif;
    font-size: var(--font-size);
    font-weight: 400;
    line-height: 1.2;
    width: 100%;

    &::placeholder {
      color: var(--placeholder-color);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  &__icon {
    flex-shrink: 0;
    transition: all 0.2s ease;

    &--search {
      width: var(--icon-size);
      height: var(--icon-size);
    }

    &--clear {
      width: var(--clear-icon-size);
      height: var(--clear-icon-size);
      cursor: pointer;
      opacity: 0.6;
      transition: opacity 0.2s ease;

      &:hover {
        opacity: 1;
      }
    }

    &--default {
      color: var(--icon-color-default);
    }

    &--has-value {
      color: var(--icon-color-has-value);
    }

    &--active {
      color: var(--icon-color-active);
    }

    &--disabled {
      color: var(--icon-color-disabled);
    }
  }

  &__clear-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    padding: 4px;
    margin-right: 0;
    cursor: pointer;
    border-radius: 4px;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: var(--clear-btn-hover-bg);
    }

    &:active {
      background-color: var(--clear-btn-active-bg);
    }
  }

  // Size variants
  &--s {
    --border-radius: 8px;
    --content-padding: 0 12px;
    --icon-size: 20px;
    --clear-icon-size: 16px;
    --font-size: 14px;
    --icon-gap: 6px;
    --height-search: 36px;
  }

  &--m {
    --border-radius: 8px;
    --content-padding: 0 12px 0 16px;
    --icon-size: 24px;
    --clear-icon-size: 20px;
    --font-size: 14px;
    --icon-gap: 8px;
    --height-search: 44px;
  }

  &--l {
    --border-radius: 12px;
    --content-padding: 0 16px;
    --icon-size: 24px;
    --clear-icon-size: 20px;
    --font-size: 16px;
    --icon-gap: 8px;
    --height-search: 52px;
  }

  // Variant styles
  &--primary {
    --background-color: var(--color-white);
    --border-color: #e7e7e9;
    --text-color: rgba(14, 14, 34, 0.9);
    --placeholder-color: #bcbbbb;
    --icon-color-default: #bcbbbb;
    --icon-color-has-value: rgba(14, 14, 34, 0.6);
    --icon-color-active: var(--color-accent);
    --icon-color-disabled: rgba(14, 14, 34, 0.3);
    --clear-btn-hover-bg: rgba(14, 14, 34, 0.06);
    --clear-btn-active-bg: rgba(14, 14, 34, 0.1);

    &:hover {
      --border-color: rgba(14, 14, 34, 0.14);
    }

    &.q-search--active {
      --border-color: rgba(14, 14, 34, 0.2);
      --background-color: var(--color-white);
    }
  }

  &--secondary {
    --background-color: rgba(255, 255, 255, 0);
    --border-color: rgba(14, 14, 34, 0.1);
    --text-color: rgba(14, 14, 34, 0.9);
    --placeholder-color: rgba(14, 14, 34, 0.4);
    --icon-color-default: rgba(14, 14, 34, 0.4);
    --icon-color-has-value: rgba(14, 14, 34, 0.6);
    --icon-color-active: rgba(14, 14, 34, 0.9);
    --icon-color-disabled: rgba(14, 14, 34, 0.3);
    --clear-btn-hover-bg: rgba(14, 14, 34, 0.06);
    --clear-btn-active-bg: rgba(14, 14, 34, 0.1);

    &:hover {
      --border-color: rgba(14, 14, 34, 0.2);
    }

    &.q-search--active {
      --border-color: rgba(14, 14, 34, 0.3);
      --background-color: var(--color-white);
    }
  }

  &--tertiary {
    --background-color: rgba(14, 14, 34, 0);
    --border-color: rgba(14, 14, 34, 0.1);
    --text-color: rgba(14, 14, 34, 0.8);
    --placeholder-color: rgba(14, 14, 34, 0.8);
    --icon-color-default: rgba(14, 14, 34, 0.8);
    --icon-color-has-value: rgba(14, 14, 34, 0.8);
    --icon-color-active: rgba(14, 14, 34, 0.8);
    --icon-color-disabled: rgba(14, 14, 34, 0.4);
    --clear-btn-hover-bg: rgba(14, 14, 34, 0.06);
    --clear-btn-active-bg: rgba(14, 14, 34, 0.1);

    &:hover {
      --background-color: rgba(14, 14, 34, 0.06);
    }

    &.q-search--active {
      --background-color: rgba(14, 14, 34, 0.06);
    }
  }

  // Disabled state
  &--disabled {
    opacity: 0.6;
    pointer-events: none;
  }

  // Full width modifier
  &--full-width {
    width: 100%;
    max-width: none;
  }
}
</style>
